import { Form, InputGroup, Modal, ModalBody } from 'react-bootstrap';
import './ModalDataCard.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDataItem } from 'aw-data-card';
import { useTheme } from '../../utils/contexts/ThemeContext';
import AwayterToggleButtons from '../AwayterToggleButtons/AwayterToggleButtons.component';
import { AWSelect } from 'aw-select';

interface ModalDataCardProps {
    showModal: boolean;
    data: any;
    onSave: (updatedData: IDataItem[] | EditDataItem[]) => void;
    onClose: () => void;
}

interface EditDataItem {
    name: string;
    data: IDataItem;
}

const ModalDataCard: React.FC<ModalDataCardProps> = ({ showModal, data, onSave, onClose }) => {
    const [selected, setSelected] = useState(0);
    const [editedData, setEditedData] = useState<IDataItem[]>(data[selected].data ?? []);
    const { t } = useTranslation();
    const theme = useTheme();

    useEffect(() => {
        setEditedData(data[selected]?.data ?? []);
    }, [data, selected, showModal]);

    useEffect(() => {
        setSelected(0);
    }, [data]);

    const options = data.map((page: any, index: number) => ({
        id: index,
        label: page.name,
    }));

    const handleChange = (index: number, newValue: string) => {
        const updated = editedData.map((item, idx) => {
            if (idx === index) {
                return {
                    ...item,
                    value: newValue,
                };
            }
            return item;
        });
        setEditedData(updated);
    };

    const dark = theme.theme==='dark';
    // Moved inside component to access state and context
    const renderCustomInput = (typedItem: any, index: number) => {
        switch (typedItem.type) {
            case "select":
                return (
                    <AWSelect dark={dark}
                        options={typedItem.options ?? []}
                        value={typedItem.options?.find((opt: any) => opt.value === typedItem.value) ?? null}
                        onChange={(value: any) => handleChange(index, value)}
                    />
                );
            case "number":
                return (
                    <InputGroup className={theme.theme === "dark" ? "dark-mode" : ""}>
                        <Form.Control
                            value={typedItem.value}
                            type="number"
                            onChange={(e) => handleChange(index, e.target.value)}
                            min={typedItem.min}
                            max={typedItem.max}
                            isInvalid={
                                (!!typedItem.min && parseFloat(typedItem.value) < typedItem.min) ||
                                (!!typedItem.max && parseFloat(typedItem.value) > typedItem.max)
                            }
                            required
                        />
                        {typedItem.unit && <InputGroup.Text>{typedItem.unit}</InputGroup.Text>}
                    </InputGroup>
                );
            default:
                return (
                    <Form.Control
                        type={typedItem.type}
                        value={typedItem.value}
                        onChange={(value: any) => handleChange(index, value)}
                    />
                );
        }
    };

    const handleSave = () => {
        onSave(editedData);
    };

    return (
        <Modal
            show={showModal}
            keyboard={false}
            backdrop={"static"}
            className='vw-50 modal-card'
            onHide={onClose}
            centered
        >
            <Modal.Header className='position-relative d-block' style={{ color: "var(--text-primary)" }}>
                <p className='modal-title-custom m-0 text-start text-lg-center'>{t("EditParameters")}</p>
                <button
                    type='button'
                    className='btn-close position-absolute end-0 top-50 translate-middle-y me-3'
                    aria-label='Close'
                    onClick={() => {
                        setSelected(0);
                        onClose();
                    }}
                >
                    {" "}
                </button>
            </Modal.Header>

            <ModalBody className='mb-0 border'>
                {options.length > 1 ? (
                    <AwayterToggleButtons
                        dark={theme.theme === "dark"}
                        options={options}
                        value={selected}
                        onChange={(val) => setSelected(val)}
                    />
                ) : (
                    <></>
                )}

                {data[selected] ? (
                    <div
                        style={{
                            gridTemplateColumns: editedData.length > 10 ? "1fr 1fr" : "1fr"
                        }}
                        className={`param-group d-grid ${editedData.length > 10 ? "column-gap-3" : ""
                            } one-column-sm`}
                    >
                        {editedData.map((item, index) => {
                            const typedItem = item as IDataItem & {
                                options?: any[];
                                type?: "number" | "text" | "select" | "date";
                                min?: number;
                                max?: number;
                            };
                            const cols2 = editedData.length > 10;

                            return (
                                <div key={`${typedItem.label}-${index}`} className='modal-item'>
                                    <div className='row d-flex'>
                                        <div
                                            className={`${cols2 ? "col-5" : "col-7"} d-flex align-items-center`}
                                        >
                                            <p className='modal-label mb-0'>{typedItem.label}</p>
                                        </div>
                                        <div className={`${cols2 ? "col-7" : "col-5"}`}>
                                            {renderCustomInput(typedItem, index)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p>No hi ha cap pàgina seleccionada.</p>
                )}
            </ModalBody>
            <Modal.Footer>
                <button className='btn-Custom btn-IDGreen' onClick={handleSave}>
                    {t('Save')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalDataCard;